import React from 'react'
import { Redirect } from 'react-router'

import { routes } from 'constants/routes'
import localStorageKeys from 'constants/localStorageKeys'

import { removeItem } from 'utils/localStorage'
import { WorkspaceMinimalFragment } from 'gqlTypes'
import { useUserAuthorizations, mapSsoStartUrl } from 'modules/sso/operations'

import useSignOut from 'modules/user/useSignOut'
import { DynamicLoadingPage } from 'containers/DynamicLoadingPage'
import { isSsoAuthorization } from 'graphql/multisession'
import WorkspaceAgnosticRedirectRoute from './WorkspaceAgnosticRedirectRoute'

interface FallbackWorkspaceRedirectRouteProps {
  workspaces: WorkspaceMinimalFragment[]
  personalWorkspace?: WorkspaceMinimalFragment
}

/**
 * This component redirects users when they try to access the default ENTRY
 * page and there is no direct way to know where should they go.
 *
 * We know we can't redirect the user to their last workspace seen, either
 * because they don't have one or because they don't have access to it. Once
 * here, we just try to find a matching workspace the user can see or we just
 * sign them out.
 */
export const FallbackWorkspaceRedirectRoute = ({
  workspaces,
  personalWorkspace,
}: FallbackWorkspaceRedirectRouteProps) => {
  const { authorizations, hasPersonalAuthorization } = useUserAuthorizations()
  const signOut = useSignOut(routes.SIGN_UP.create({}))

  removeItem(localStorageKeys.lastWorkspaceIdKey)

  if (hasPersonalAuthorization) {
    /**
     * Let's see if the user has a personal workspace where we can send them to.
     */
    if (personalWorkspace) {
      return (
        <Redirect
          to={routes.WORKSPACE_SHARES.create({
            workspaceId: personalWorkspace.identifier,
          })}
        />
      )
    }

    /**
     * User has no personal workspace so let's just look for the first non-SSO
     * workspace and redirect them there.
     */
    const firstNonSsoWorkspace = workspaces.find(
      workspace => !workspace.customer?.ssoEnabled
    )

    if (firstNonSsoWorkspace) {
      if (firstNonSsoWorkspace.userRole === 'FINANCE') {
        return (
          <WorkspaceAgnosticRedirectRoute
            workspaceId={firstNonSsoWorkspace.identifier}
            fallbackRoute={routes.WORKSPACE_SETTINGS_GENERAL.create({
              workspaceId: firstNonSsoWorkspace.identifier,
            })}
          />
        )
      } else {
        return (
          <WorkspaceAgnosticRedirectRoute
            workspaceId={firstNonSsoWorkspace.identifier}
            fallbackRoute={routes.WORKSPACE_SHARES.create({
              workspaceId: firstNonSsoWorkspace.identifier,
            })}
          />
        )
      }
    }
  }

  /**
   * If the user is authenticated but only has SSO sessions OR they only have
   * access to SSO workspaces, then we look for the best matching workspace we
   * can find from their session information.
   */
  const availableWorkspaceIds = authorizations.map(auth =>
    isSsoAuthorization(auth) ? auth.workspaceId : null
  )

  const availableWorkspace = workspaces.find(workspace =>
    availableWorkspaceIds.includes(workspace.identifier)
  )

  /**
   * We found a SSO workspace the user have access to with their SSO
   * credentials.
   */
  if (availableWorkspace) {
    if (availableWorkspace.userRole === 'FINANCE') {
      return (
        <Redirect
          to={routes.WORKSPACE_SETTINGS_GENERAL.create({
            workspaceId: availableWorkspace.identifier,
          })}
        />
      )
    } else {
      return (
        <Redirect
          to={routes.WORKSPACE_SHARES.create({
            workspaceId: availableWorkspace.identifier,
          })}
        />
      )
    }
  }

  /**
   * At this point, the user only has SSO workspaces they don't have access to,
   * if any. We will try signing them into the first SSO workspace we find.
   */
  const firstSsoWorkspace = workspaces.find(
    workspace => workspace.customer?.ssoEnabled
  )

  if (firstSsoWorkspace && firstSsoWorkspace.customer?.ssoStartUrl) {
    window.location.href = mapSsoStartUrl(
      firstSsoWorkspace.customer.ssoStartUrl
    )

    return <DynamicLoadingPage />
  }

  /**
   * If everything else fails we just sign out the user.
   * This happens ONLY once we made sure there is no workspace we can redirect
   * the user to:
   *   * The user has no last workspace seen.
   *   * The user has no access to their last workspace seen.
   *   * The user has no access to any non-SSO workspace.
   *   * The user does not belong to any SSO workspace.
   */
  signOut()
  return <DynamicLoadingPage />
}
